import axios from 'axios';
import { uipApiInstance } from '../services';
import { setEwsTerm, setEwsStepperCtaStatus, computeEwsStepperCtaStatus, setEwsLoading } from '../features/ews';
import store from '../redux/store';
import { setSelectedFundingTermIndex } from '../features/global/globalSlice';
import { EwsDurationKey, TermTabType } from '../common/constants';

// Unified stream event types. A single stream carries every duration's data:
// - 'ews'             → ews + contribution schedules for one duration
// - 'runpipe'         → a single goal's runpipe result for one duration
// - 'complete'        → one duration finished streaming
// - 'stream_complete' → every duration finished streaming
// - 'error'           → stream/backend error (per-duration when it carries a `duration`)

export type EwsUnifiedStreamEventType = 'ews' | 'runpipe' | 'complete' | 'stream_complete' | 'error';

export interface EwsUnifiedStreamEvent {
    type: EwsUnifiedStreamEventType;
    duration?: string;
    payload?: any;
}

// Map a stream duration key to its Funding Navigator term tab index.
const DURATION_KEY_TO_TERM_TAB: Record<EwsDurationKey | any, TermTabType> = {
    shortTerm: TermTabType.SHORT,
    mediumTerm: TermTabType.MEDIUM,
    longTerm: TermTabType.LONG
};

// Single in-flight controller — the unified stream carries every duration in one request.
let unifiedAbortController: AbortController | null = null;
// Every live stream (primary + retries), so leaving a plan can cancel them all.
const activeControllers = new Set<AbortController>();

interface TermAccumulator {
    ews: any[];
    runPipeResponsesMap: Map<string, any>;
    contributionSchedules: any;
    ewsGoePayload: any;
    runpipeGoePayloadMap: Map<string, any>;
    error: any;
    loading: boolean;
}

/** Parse incoming SSE text into individual frames. Handles multi-line `data:` fields. */
function parseSseChunk(chunk: string): EwsUnifiedStreamEvent[] {
    const lines = chunk.split('\n');
    const events: EwsUnifiedStreamEvent[] = [];
    let currentEvent: Partial<EwsUnifiedStreamEvent> = {};
    let dataLines: string[] = [];

    // Finalize the frame currently being accumulated and push it onto `events`.
    const flushEvent = () => {
        if (dataLines.length > 0) {
            try {
                const jsonData = JSON.parse(dataLines.join('\n'));
                currentEvent = {
                    type: jsonData.type || currentEvent.type,
                    duration: jsonData.duration || currentEvent.duration,
                    payload: jsonData.payload || jsonData
                };
            } catch {
                console.warn('[EWS Unified Stream] Failed to parse JSON:', dataLines.join('\n'));
            }
        }
        if (currentEvent.type) {
            events.push(currentEvent as EwsUnifiedStreamEvent);
        }
        currentEvent = {};
        dataLines = [];
    };

    for (const line of lines) {
        // Trailing '\r' (CRLF line endings) would corrupt JSON.parse and event names.
        const cleanLine = line.endsWith('\r') ? line.slice(0, -1) : line;
        if (cleanLine.startsWith(':')) {
            // Heartbeat comment, ignore
            continue;
        }
        if (cleanLine === '') {
            // Blank line signals end of event frame
            flushEvent();
            continue;
        }
        if (cleanLine.startsWith('event:')) {
            currentEvent.type = cleanLine.slice(6).trim() as EwsUnifiedStreamEventType;
            continue;
        }
        if (cleanLine.startsWith('data:')) {
            dataLines.push(cleanLine.slice(5).trim());
            continue;
        }
    }

    // whatever is still buffered once the chunk is exhausted.
    flushEvent();
    return events;
}

/** Recompute the aggregate stepper/CTA gating status from the live ews slice and push it to redux. */
function refreshEwsStepperCtaStatus(dispatch: any): void {
    dispatch(setEwsStepperCtaStatus(computeEwsStepperCtaStatus(store.getState().ews?.responses ?? {})));
}

/** Merge an EWS response for a single term into the dedicated ews slice. */
function mergeEwsTermIntoStore(dispatch: any, term: EwsDurationKey, termSlice: any): void {
    // Only merge the term here; the stepper/CTA rollup is refreshed once per stream (seed + finally)
    // to avoid recomputing it on every per-term tick.
    dispatch(setEwsTerm({ term, response: termSlice }));
}

/** Build the term response slice from the accumulator. */
function buildTermSliceFromAccumulator(accumulator: TermAccumulator, isError: boolean): any {
    const runPipeResponses = Array.from(accumulator.runPipeResponsesMap.values());
    const runpipeGoePayloads = Array.from(accumulator.runpipeGoePayloadMap.values());
    const hasData = accumulator.ews.length > 0 || runPipeResponses.length > 0;

    const termSlice: any = {
        loading: accumulator.loading,
        // Only flag a full error when the term has NO partial data; otherwise keep the already-streamed
        ...(isError && !hasData && { error: true, err: accumulator.error })
    };

    if (hasData) {
        termSlice.response = {
            body: {
                ews: accumulator.ews,
                runPipeResponses
            }
        };
        if (accumulator.contributionSchedules) {
            termSlice.response.body.contributionSchedules = accumulator.contributionSchedules;
        }
    }

    // Store goePayload as { ews, runpipe[] } so extractAndSyncRunpipeResponses can read goePayload.runpipe.
    if (accumulator.ewsGoePayload || runpipeGoePayloads.length > 0) {
        termSlice.goePayload = {
            ews: accumulator.ewsGoePayload,
            runpipe: runpipeGoePayloads
        };
    }

    return termSlice;
}

/**
 * Open the single unified SSE stream via `uipApiInstance` (reuses its auth/CSRF/from_tenant/401
 * interceptors), parse events, and invoke the callback. Resolves when the request completes
 * (backend closes the stream after 'stream_complete'/'error').
 */
export async function openUnifiedEwsStream(
    data: any,
    clientemail: string,
    // eslint-disable-next-line no-unused-vars
    onEvent: (event: EwsUnifiedStreamEvent) => void,
    signal?: AbortSignal
): Promise<void> {
    let processedLength = 0;
    let buffer = '';

    // Parse any newly-arrived text (since the last progress tick) into SSE frames.
    const processNewText = (fullText: string) => {
        if (fullText.length <= processedLength) {
            return;
        }
        buffer += fullText.slice(processedLength);
        processedLength = fullText.length;

        // Split by blank lines (which mark event boundaries in SSE)
        const parts = buffer.split('\n\n');
        // Keep the last incomplete part in the buffer
        buffer = parts[parts.length - 1];

        for (let i = 0; i < parts.length - 1; i++) {
            const events = parseSseChunk(parts[i] + '\n');
            events.forEach((evt) => {
                if (evt.type === 'error') {
                    console.error('[EWS Unified Stream] Error event received:', evt.payload);
                }
                onEvent(evt);
            });
        }
    };

    try {
        const response = await uipApiInstance.post('/api/advice/ews', data, {
            responseType: 'text',
            headers: {
                Accept: 'text/event-stream',
                clientemail
            },
            signal,
            // responseText accumulated so far, letting us parse SSE frames as they arrive.
            onDownloadProgress: (progressEvent: any) => {
                const responseText = progressEvent?.event?.target?.responseText;
                if (typeof responseText === 'string') {
                    processNewText(responseText);
                }
            }
        });

        // Catch any content that arrived after the last progress tick
        if (typeof response.data === 'string') {
            processNewText(response.data);
        }
        if (buffer.trim()) {
            const events = parseSseChunk(buffer);
            events.forEach((evt) => onEvent(evt));
        }
        console.log('[EWS Unified Stream] Stream completed successfully. open');
    } catch (err: any) {
        if (axios.isCancel(err) || err.name === 'CanceledError' || err.name === 'AbortError') {
            console.log('[EWS Unified Stream] Stream was aborted.');
        } else {
            console.error('[EWS Unified Stream] Error opening stream:', err);
            onEvent({
                type: 'error',
                payload: { error: err?.message || String(err) }
            });
        }
    }
}

/** Abort the in-flight unified stream, if any. */
export function abortUnifiedEwsStream(): void {
    if (unifiedAbortController) {
        unifiedAbortController.abort();
        unifiedAbortController = null;
    }
}

/** Abort every in-flight EWS stream (primary and retries), e.g. when the user leaves the plan. */
export function cancelAllEwsStreams(): void {
    if (!activeControllers.size) return;
    activeControllers.forEach((controller) => controller.abort());
    activeControllers.clear();
    unifiedAbortController = null;
    // Aborted runs skip their own finally-cleanup, so reset the transient flag here.
    store.dispatch(setEwsLoading(false));
}

/**
 * Open the single unified stream, accumulate ews/runpipe chunks per duration (each chunk is tagged
 * with its `duration`), and dispatch incremental per-term updates to redux. Resolves once the
 * backend closes the stream; resolves `false` if the stream was aborted (caller must not merge).
 */
export async function runUnifiedEwsStream(
    dispatch: any,
    data: any,
    clientemail: string,
    options: { abortPrevious?: boolean } = {}
): Promise<boolean> {
    const { abortPrevious = true } = options;

    const controller = new AbortController();
    activeControllers.add(controller);

    // later primary run. A concurrent retry stream (abortPrevious=false) runs independently: it must
    const isPrimary = abortPrevious;
    if (isPrimary) {
        abortUnifiedEwsStream();
        unifiedAbortController = controller;
    }
    dispatch(setEwsLoading(true));
    // Reflect the seeded (loading) terms in the stepper/CTA rollup immediately.
    refreshEwsStepperCtaStatus(dispatch);
    // Once the backend signals stream_complete, a trailing XHR "Network Error" (status 200) is
    // spurious and must not wipe the data already delivered.
    let streamCompleted = false;
    // Auto-switch the funding term tab to whichever duration's ews data streams in first (once).
    let firstEwsHandled = false;

    // One accumulator per duration, created lazily as that duration's first chunk arrives.
    const accumulators = new Map<EwsDurationKey, TermAccumulator>();
    const getAccumulator = (term: EwsDurationKey): TermAccumulator => {
        let acc = accumulators.get(term);
        if (!acc) {
            acc = {
                ews: [],
                runPipeResponsesMap: new Map<string, any>(),
                contributionSchedules: undefined,
                ewsGoePayload: undefined,
                runpipeGoePayloadMap: new Map<string, any>(),
                error: null,
                loading: false
            };
            accumulators.set(term, acc);
        }
        return acc;
    };

    // Whole-stream failure (network error / server error): mark every available term as failed, not
    const failAllAvailableTerms = (errText: any) => {
        const responses = store.getState().ews?.responses ?? {};
        (Object.keys(responses) as EwsDurationKey[]).forEach((term) => {
            const acc = accumulators.get(term);
            if (acc) {
                acc.error = errText;
                acc.loading = false;
                mergeEwsTermIntoStore(dispatch, term, buildTermSliceFromAccumulator(acc, true));
            } else if (responses[term]?.loading) {
                mergeEwsTermIntoStore(dispatch, term, { loading: false, error: true, err: errText });
            }
        });
    };

    const onEvent = (event: EwsUnifiedStreamEvent) => {
        if (controller.signal.aborted) return;
        const term = event.payload?.duration ?? event.duration;

        if (event.type === 'ews') {
            if (!term) return;
            const acc = getAccumulator(term);
            // A 'success: false' ews payload means this term failed — flag it as errored in the store.
            if (event.payload?.success === false) {
                acc.error = event.payload?.message || event.payload?.error || 'Unknown error';
                acc.loading = false;
                mergeEwsTermIntoStore(dispatch, term, buildTermSliceFromAccumulator(acc, true));
                return;
            }
            if (event.payload?.body?.ews) {
                acc.ews = event.payload.body.ews;
            }
            if (event.payload?.body?.contributionSchedules) {
                acc.contributionSchedules = event.payload.body.contributionSchedules;
            }
            if (event.payload?.goePayload) {
                acc.ewsGoePayload = event.payload.goePayload.ews;
            }
            // On the very first ews data (any duration), land the tab on that duration.
            if (!firstEwsHandled) {
                firstEwsHandled = true;
                dispatch(setSelectedFundingTermIndex(DURATION_KEY_TO_TERM_TAB[term]));
            }
            mergeEwsTermIntoStore(dispatch, term, buildTermSliceFromAccumulator(acc, false));
        } else if (event.type === 'runpipe') {
            if (!term) return;
            const acc = getAccumulator(term);

            if (event.payload?.goalId) {
                acc.runPipeResponsesMap.set(event.payload.goalId, {
                    ...Object.fromEntries(
                        Object.entries(event.payload).filter(
                            ([key]) => !['goePayload', 'duration', 'type'].includes(key)
                        )
                    )
                });
            }

            const runpipeGoe = { goalId: event.payload?.goalId, runpipe_request: event.payload?.goePayload?.runpipe };
            if (runpipeGoe) {
                const entries = Array.isArray(runpipeGoe) ? runpipeGoe : [runpipeGoe];
                entries.forEach((entry: any) => {
                    const gId = entry?.goalId ?? event.payload?.goalId;
                    if (gId) {
                        acc.runpipeGoePayloadMap.set(gId, entry);
                    }
                });
            }
            mergeEwsTermIntoStore(dispatch, term, buildTermSliceFromAccumulator(acc, false));
        } else if (event.type === 'complete') {
            if (!term) return;
            // const acc = getAccumulator(term);
            // acc.loading = false;
            // mergeEwsTermIntoStore(dispatch, term, buildTermSliceFromAccumulator(acc, false));
        } else if (event.type === 'stream_complete') {
            streamCompleted = true;
            // accumulators.forEach((acc, t) => {
            //   acc.loading = false;
            //   mergeEwsTermIntoStore(dispatch, t, buildTermSliceFromAccumulator(acc, false));
            // });
            if (isPrimary) unifiedAbortController = null;
        } else if (event.type === 'error') {
            if (streamCompleted) return;
            const errorMsg = event.payload?.message || event.payload?.error || 'Unknown error';
            const excType = event.payload?.exc_type ? ` (${event.payload.exc_type})` : '';
            const errText = `${errorMsg}${excType}`;

            if (term) {
                const acc = getAccumulator(term);
                acc.error = errText;
                acc.loading = false;
                mergeEwsTermIntoStore(dispatch, term, buildTermSliceFromAccumulator(acc, true));
            } else {
                failAllAvailableTerms(errText);
            }
        }
    };

    try {
        await openUnifiedEwsStream(data, clientemail, onEvent, controller.signal);
    } catch (err) {
        console.error('[EWS Unified Stream] Uncaught error:', err);
        failAllAvailableTerms(err);
    } finally {
        activeControllers.delete(controller);
        if (!controller.signal.aborted) {
            dispatch(setEwsLoading(false));
            // Final rollup once the stream settles (covers uncaught-error terms merged above).
            refreshEwsStepperCtaStatus(dispatch);
        }
    }
    return !controller.signal.aborted;
}

/**
 * After the stream closes cleanly, mark every requested goal (goalProfileList) that never produced a
 * runpipe response as errored for its term+goalId, so goals stuck on the loading skeleton fall back
 * to the failed/retry state instead of spinning forever.
 */
// function finalizeMissingRunpipesAsError(
//   dispatch: any,
//   accumulators: Map<EwsDurationKey, TermAccumulator>
// ): void {
//   const ewsRequests = store.getState().adviceGoals?.global?.ewsRequests ?? {};
//   accumulators.forEach((acc, term) => {
//     // Only terms that streamed some data have goals awaiting runpipe; a term with nothing is a full
//     // error handled elsewhere.
//     const hasData = acc.ews.length > 0;
//     if (!hasData) return;

//     const expectedGoalIds: string[] = (ewsRequests[term]?.goalProfileList ?? [])
//       .map((g: any) => g?.goalId)
//       .filter(Boolean);

//     let changed = false;
//     expectedGoalIds.forEach((goalId) => {
//       if (!acc.runPipeResponsesMap.has(goalId)) {
//         acc.runPipeResponsesMap.set(goalId, {
//           goalId,
//           response: { success: false },
//           noEligibleFundings: false,
//           error: true
//         });
//         changed = true;
//       }
//     });

//     if (changed) {
//       mergeEwsTermIntoStore(dispatch, term, buildTermSliceFromAccumulator(acc, false));
//     }
//   });
// }
